import { seededRandom } from '../trick/rng.js';

/** A tile face id: `dots-1`..`dots-9`, `bamboo-1`..`bamboo-9`, `characters-1`..`characters-9`, `wind-east|south|west|north`, `dragon-red|green|white`, `flower-1`..`flower-4`, `season-1`..`season-4`. */
export type Face = string;

const SUITS = ['dots', 'bamboo', 'characters'] as const;
const WINDS = ['east', 'south', 'west', 'north'] as const;
const DRAGONS = ['red', 'green', 'white'] as const;

/** The 34 faces that have four copies each (136 tiles). */
export const PLAIN_FACES: readonly Face[] = [
  ...SUITS.flatMap((s) => Array.from({ length: 9 }, (_, i) => `${s}-${i + 1}`)),
  ...WINDS.map((w) => `wind-${w}`),
  ...DRAGONS.map((d) => `dragon-${d}`),
];
export const FLOWER_FACES: readonly Face[] = [1, 2, 3, 4].map((n) => `flower-${n}`);
export const SEASON_FACES: readonly Face[] = [1, 2, 3, 4].map((n) => `season-${n}`);

/** Two tiles match when their keys are equal: the same face, or any flower with any flower, or any season with any season. */
export function matchKey(face: Face): string {
  if (face.startsWith('flower-')) return 'flower';
  if (face.startsWith('season-')) return 'season';
  return face;
}
export const facesMatch = (a: Face, b: Face): boolean => matchKey(a) === matchKey(b);

/** The full 144-tile set, as 72 matching pairs in a seeded random order. A smaller layout uses the first N/2 pairs. */
export function shuffledPairs(random: () => number): [Face, Face][] {
  const pairs: [Face, Face][] = [];
  for (const f of PLAIN_FACES) pairs.push([f, f], [f, f]);
  pairs.push([FLOWER_FACES[0]!, FLOWER_FACES[1]!], [FLOWER_FACES[2]!, FLOWER_FACES[3]!]);
  pairs.push([SEASON_FACES[0]!, SEASON_FACES[1]!], [SEASON_FACES[2]!, SEASON_FACES[3]!]);
  return shuffle(pairs, random);
}

export function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Unicode Mahjong Tiles block (U+1F000..U+1F029): free art that needs no image generation. */
export function faceGlyph(face: Face): string {
  const [kind, which] = face.split('-') as [string, string];
  const at = (code: number) => String.fromCodePoint(code);
  switch (kind) {
    case 'wind':
      return at(0x1f000 + WINDS.indexOf(which as (typeof WINDS)[number]));
    case 'dragon':
      return at(0x1f004 + DRAGONS.indexOf(which as (typeof DRAGONS)[number]));
    case 'characters':
      return at(0x1f007 + Number(which) - 1);
    case 'bamboo':
      return at(0x1f010 + Number(which) - 1);
    case 'dots':
      return at(0x1f019 + Number(which) - 1);
    case 'flower':
      return at(0x1f022 + Number(which) - 1);
    case 'season':
      return at(0x1f026 + Number(which) - 1);
    default:
      throw new Error(`unknown tile face ${face}`);
  }
}

const SUIT_NAMES: Record<string, string> = { dots: 'dots', bamboo: 'bamboo', characters: 'characters' };
const NUMBER_WORDS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

/** A name a screen reader can say: "three of bamboo", "red dragon", "east wind", "flower 2". */
export function faceName(face: Face): string {
  const [kind, which] = face.split('-') as [string, string];
  if (kind in SUIT_NAMES) return `${NUMBER_WORDS[Number(which) - 1]} of ${SUIT_NAMES[kind]}`;
  if (kind === 'wind') return `${which} wind`;
  if (kind === 'dragon') return `${which} dragon`;
  return `${kind} ${which}`;
}

export { seededRandom };
